import { supabase } from '@/supabase';
import type { Department } from '~/types';
import { useAuthStore } from '#imports';

export const useDepartmentStore = defineStore('departmentStore', () => {
    const authStore = useAuthStore();
    const { isDemo, profile } = storeToRefs(authStore);

    const _isPending = ref<boolean>(false);
    const departments = ref<Array<any>>([]);
    const department = ref<any>({});
    const departments_cnt = ref<number>(0);

    const isPending = computed(() => { return _isPending.value });
    
    const params = ref<any>({
        isAsc: true,
        orderBy: 'name',
        page: 1,
        perPage: Number.MAX_SAFE_INTEGER,
        search: ''
    });

    const blankDepartment = computed(() => { return { id: 0, name: '', sort_order: 1, location_id: 0 } as Department; });
    const startIndex = computed(() => { return (params.value.page - 1) * params.value.perPage; });
    const endIndex = computed(() => { return startIndex.value + params.value.perPage - 1 });

    function $reset(): void {
        departments.value = [];
        department.value = {};
        departments_cnt.value = 0;
        _isPending.value = false;
        params.value = {
            isAsc: true,
            orderBy: 'name',
            page: 1,
            perPage: Number.MAX_SAFE_INTEGER,
            search: ''
        };
    }

    async function fetchDepartments(location_id: number, fetchAll: boolean = false): Promise<string> {
        if (_isPending.value || !profile.value) return 'Error:  Please try again';
        _isPending.value = true;

        let msg = '';

        let query =
        isDemo.value
        ? supabase
            .from('department_demo')
            .select('id, name, location_demo:location_id(*), created_at, sort_order', { count: 'exact', head: false })
            .eq('location_id', location_id)
            .order(params.value.orderBy, { ascending: params.value.isAsc })
        : supabase
            .from('department')
            .select('id, name, location:location_id(*), created_at, sort_order', { count: 'exact', head: false })
            .eq('location_id', location_id)
            .order(params.value.orderBy, { ascending: params.value.isAsc });;

        if (!fetchAll) {
            query = query.range(startIndex.value, endIndex.value);
        }

        const { data, count, error } = await query;

        if (error) {
            msg = error.message as string;
        }
        else if (data) {
            departments.value = (<unknown>data as Array<Department>).map(d => {
                return {
                    ...d,
                    location: isDemo.value ? d.location_demo : d.location
                }
            });
            departments_cnt.value = count || 0;
        }

        if (!department.value) {
            department.value = blankDepartment.value;
        }

        _isPending.value = false;
        return msg;
    }

    async function addUpdateDepartment(item: any): Promise<string> {
        if (isDemo.value) {
            return 'The setup section cannot be modified in demo mode.';
        }
        
        if (_isPending.value) return 'Error:  Please try again';
        _isPending.value = true;

        let msg = '';

        let query = null;

        if (item.id === 0) {
            query = supabase
                .from(isDemo.value ? 'department_demo' : 'department')
                .insert([
                    {
                        created_at: new Date().toISOString(),
                        name: item.name,
                        location_id: item.location_id,
                        sort_order: item.sort_order
                    }
                ])
                .select();
        }
        else {
            query = supabase
                .from(isDemo.value ? 'department_demo' : 'department')
                .update([
                    {
                        name: item.name,
                        location_id: item.location_id,
                        sort_order: item.sort_order
                    }
                ])
                .eq('id', item.id)
                .select();
        }

        const { error } = await query;

        if (error) {
            msg = error.message as string;
        }

        _isPending.value = false;
        return msg;
    }

    async function deleteDepartment(id: number): Promise<string> {
        if (_isPending.value) return 'Error:  Please try again';
        _isPending.value = true;

        let msg = '';

        const { error } = await supabase
            .from(isDemo.value ? 'department_demo' : 'department')
            .delete()
            .eq('id', id);

        if (error) {
            msg = error.message as string;
        }

        _isPending.value = false;
        return msg;
    }

    return {
        blankDepartment,
        departments,
        department,
        departments_cnt,
        params,
        isPending,
        addUpdateDepartment,
        deleteDepartment,
        fetchDepartments,
        $reset
    }
});

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useDepartmentStore, import.meta.hot))
}